import React, { useState, useEffect, useRef } from 'react';
import { Pin, Send, Lock, MessageSquare, ShieldAlert, ArrowDown } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useGroupStore } from '../store/groupStore';
import CooldownTimer from '../components/CooldownTimer';
import GlassCard from '../components/GlassCard';
import { useToast } from '../components/Toast';
import { AVATARS, CHAT_TAGS, cn } from '../lib/utils';
import { chatApi } from '../lib/apiClient';

export default function ChatPage() {
  const { user } = useAuthStore();
  const { 
    groupId, 
    chatMessages, 
    sendMessage, 
    pinnedMessage, 
    fetchMessages 
  } = useGroupStore();

  const toast = useToast();

  const [messageText, setMessageText] = useState('');
  const [selectedTag, setSelectedTag] = useState('Study Help');
  const [cooldownEnd, setCooldownEnd] = useState(null);
  
  // Floating banner states
  const [showScrollBanner, setShowScrollBanner] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const chatContainerRef = useRef(null);
  const prevMessagesLength = useRef(chatMessages.length);

  // Poll for new messages every 10 seconds
  useEffect(() => {
    let active = true;
    const pollMessages = async () => {
      if (!groupId) return;
      try {
        // TODO: connect to real API — GET /api/chat?groupId=...
        const res = await chatApi.getMessages(groupId).catch(() => null);
        if (active && res) {
          // If we had a real API, we would update state here.
          // In mock, fetchMessages from store is loaded.
          await fetchMessages();
        }
      } catch (err) {
        console.error(err);
      }
    };

    pollMessages();
    const interval = setInterval(pollMessages, 10000);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [groupId, fetchMessages]);

  // Handle rate limits checking (Max 10 messages per user per hour rolling)
  const checkRateLimit = () => {
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 3600 * 1000);

    // Filter user's messages in last hour
    const userMsgsInLastHour = chatMessages
      .filter(msg => msg.userId === user?.id)
      .filter(msg => new Date(msg.timestamp) > oneHourAgo)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    if (userMsgsInLastHour.length >= 10) {
      // Cooldown ends 1 hour after the earliest message in the window
      const earliestMsg = userMsgsInLastHour[0];
      const cooldownTime = new Date(new Date(earliestMsg.timestamp).getTime() + 3600 * 1000);
      setCooldownEnd(cooldownTime);
      return true;
    } else {
      setCooldownEnd(null);
      return false;
    }
  };

  // Check rate limit initially and when messages change
  useEffect(() => {
    checkRateLimit();
  }, [chatMessages, user?.id]);

  // Handle Scroll to Bottom
  const scrollToBottom = (behavior = 'smooth') => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior
      });
      setShowScrollBanner(false);
      setUnreadCount(0);
    }
  };

  // Scroll on mount and when new messages arrive
  useEffect(() => {
    const isAtBottom = chatContainerRef.current 
      ? chatContainerRef.current.scrollHeight - chatContainerRef.current.scrollTop - chatContainerRef.current.clientHeight < 100
      : true;

    if (chatMessages.length > prevMessagesLength.current) {
      if (isAtBottom || chatMessages[chatMessages.length - 1].userId === user?.id) {
        // Delay slightly for render cycles
        setTimeout(() => scrollToBottom('smooth'), 100);
      } else {
        setShowScrollBanner(true);
        setUnreadCount(prev => prev + 1);
      }
    } else if (prevMessagesLength.current === 0) {
      setTimeout(() => scrollToBottom('auto'), 100);
    }
    prevMessagesLength.current = chatMessages.length;
  }, [chatMessages, user?.id]);

  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    const isAtBottom = chatContainerRef.current.scrollHeight - chatContainerRef.current.scrollTop - chatContainerRef.current.clientHeight < 100;
    if (isAtBottom) {
      setShowScrollBanner(false);
      setUnreadCount(0);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;

    // Check rate limit one last time
    if (checkRateLimit()) {
      toast.error('Hourly rate limit hit! Please wait for cooldown.');
      return;
    }

    try {
      const avatarIndex = user?.avatar ?? 0;
      const userName = user?.displayName || user?.name || 'Anonymous';
      
      const result = await sendMessage(
        user.id,
        userName,
        avatarIndex,
        selectedTag,
        messageText.trim()
      );

      if (result?.success) {
        setMessageText('');
      }
    } catch (err) {
      toast.error('Failed to send message.');
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-60px)] md:h-full relative bg-[#090e1a]">
      
      {/* 1. Sticky Pinned Message Banner */}
      {pinnedMessage && (
        <div className="z-10 p-3 mx-4 mt-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-start gap-3 backdrop-blur-md">
          <Pin size={16} className="text-indigo-400 mt-0.5 flex-shrink-0 rotate-45 animate-bounce" />
          <div className="flex-1 overflow-hidden">
            <span className="text-white/30 text-[10px] uppercase font-bold tracking-wider">
              Pinned by {pinnedMessage.userName}
            </span>
            <p className="text-white/80 text-xs font-medium truncate mt-0.5">
              {pinnedMessage.text}
            </p>
          </div>
        </div>
      )}

      {/* 2. Messages Chat Area */}
      <div 
        ref={chatContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
      >
        {chatMessages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-8">
            <MessageSquare size={48} className="text-white/10 mb-4 animate-pulse" />
            <h3 className="heading text-lg font-bold text-white/50">Silence in the Hall</h3>
            <p className="text-white/30 text-xs max-w-xs mx-auto mt-1 leading-relaxed">
              No conversations have been recorded in this league. Be the first to break the silence.
            </p>
          </div>
        ) : (
          chatMessages.map((msg, index) => {
            const isOwn = msg.userId === user?.id;
            const avatarColor = AVATARS[msg.userAvatar % AVATARS.length]?.bg || '#6366f1';
            const avatarEmoji = AVATARS[msg.userAvatar % AVATARS.length]?.emoji || '🎓';

            // Tag style mapping
            const tagObj = CHAT_TAGS.find(t => t.value === msg.tag) || CHAT_TAGS[3];

            // Timestamp parsing
            const formatTimeShort = (tStr) => {
              try {
                const date = new Date(tStr);
                return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
              } catch {
                return '00:00';
              }
            };

            return (
              <div 
                key={msg.id}
                className={cn(
                  "flex items-start gap-3 animate-slide-up",
                  isOwn ? "flex-row-reverse" : "flex-row"
                )}
                style={{ animationDelay: `${Math.min(index * 0.05, 0.4)}s` }}
              >
                {/* User Avatar */}
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm shadow-md flex-shrink-0"
                  style={{ background: avatarColor, boxShadow: `0 0 0 2px #090e1a, 0 0 0 3px ${avatarColor}40` }}
                  title={msg.userName}
                >
                  {avatarEmoji}
                </div>

                {/* Message Bubble Container */}
                <div className={cn(
                  "flex flex-col max-w-[70%]",
                  isOwn ? "items-end" : "items-start"
                )}>
                  {/* Sender Header */}
                  <div className="flex items-center gap-2 mb-1 px-1 text-[10px]">
                    <span className="font-semibold text-white/50">{msg.userName}</span>
                    <span 
                      className="px-1.5 py-0.5 rounded-full font-bold text-[8px]"
                      style={{ background: `${tagObj.color}15`, color: tagObj.color }}
                    >
                      {msg.tag}
                    </span>
                    <span className="text-white/20">{formatTimeShort(msg.timestamp)}</span>
                  </div>

                  {/* Message Bubble */}
                  <div className={cn(
                    "rounded-2xl px-4 py-2.5 text-sm leading-relaxed backdrop-blur-md shadow-lg font-medium",
                    isOwn 
                      ? "chat-bubble-own rounded-tr-none text-white font-medium" 
                      : "chat-bubble-other rounded-tl-none text-white/90"
                  )}>
                    {msg.text}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Floating Scroll Banner */}
      {showScrollBanner && (
        <button
          onClick={() => scrollToBottom('smooth')}
          className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 px-3 py-2 rounded-full text-xs font-bold text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 hover:bg-indigo-500/20 backdrop-blur-md shadow-md animate-bounce"
        >
          <ArrowDown size={14} />
          <span>{unreadCount} new message{unreadCount > 1 ? 's' : ''}</span>
        </button>
      )}

      {/* 3. Message Input Bar */}
      <form 
        onSubmit={handleSend}
        className="p-4 border-t border-white/5 bg-white/[0.02] backdrop-blur-lg flex flex-col md:flex-row gap-3 items-stretch md:items-center"
      >
        {/* Topic Tag Selector Dropdown */}
        <select
          value={selectedTag}
          onChange={(e) => setSelectedTag(e.target.value)}
          className="rounded-xl px-3 py-2.5 text-xs text-white/70 outline-none border border-white/10"
          style={{ background: 'rgba(255,255,255,0.05)' }}
        >
          {CHAT_TAGS.map(tag => (
            <option key={tag.value} value={tag.value} style={{ background: '#090e1a' }}>
              {tag.value}
            </option>
          ))}
        </select>

        {/* Input Bar */}
        <div className="flex-1 flex items-center gap-3 relative">
          <input
            type="text"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            disabled={cooldownEnd !== null}
            placeholder={cooldownEnd !== null ? "Hourly rate limit reached." : "Type a message in the league..."}
            className="w-full rounded-xl pl-4 pr-12 py-2.5 text-sm text-white placeholder-white/20 outline-none border border-white/10 focus:border-indigo-500/60"
            style={{ background: 'rgba(255,255,255,0.03)' }}
          />

          {/* Locked indicators / Send buttons */}
          <div className="absolute right-2 top-1.5 flex items-center">
            {cooldownEnd !== null ? (
              <div className="p-1.5 text-red-400 flex items-center justify-center">
                <Lock size={16} className="animate-pulse" />
              </div>
            ) : (
              <button
                type="submit"
                disabled={!messageText.trim()}
                className={cn(
                  "p-1.5 rounded-lg flex items-center justify-center transition-all duration-200",
                  messageText.trim() 
                    ? "bg-indigo-600 hover:bg-indigo-500 text-white shadow-md shadow-indigo-600/30" 
                    : "text-white/10 cursor-default"
                )}
              >
                <Send size={14} className="fill-current" />
              </button>
            )}
          </div>
        </div>

        {/* Cooldown Lock Display (if rate limit hit) */}
        {cooldownEnd !== null && (
          <div className="flex-shrink-0 self-center">
            <CooldownTimer endTime={cooldownEnd} />
          </div>
        )}
      </form>

    </div>
  );
}
